<template>
    <header class="overview-header">
        <h1>Property Management</h1>
        <button class="add-button" @click="toggleForm">
            {{ isEditing ? 'Cancel Edit' : (showForm ? 'Close Form' : 'Add New Property') }}
        </button>
    </header>

    <!-- Add / Edit Form -->
    <section v-if="showForm" class="form-section">
        <h2>{{ isEditing ? 'Edit Property' : 'New Property' }}</h2>
        <form @submit.prevent="saveProperty">
            <div class="form-group">
                <label for="propertyName">Name of Property</label>
                <input id="propertyName" v-model="propertyForm.propertyName" type="text" required />
            </div>
            
            <div class="form-group">
                <label for="address">Address of Property</label>
                <textarea id="address" v-model="propertyForm.address" rows="3" required></textarea>
            </div>
            
            <div class="form-group">
                <label for="billTo">Bill To</label>
                <input id="billTo" v-model="propertyForm.billTo" type="text" required />
            </div>
            
            <div class="form-group">
                <label for="billToEmail">Bill To Email Address</label>
                <input id="billToEmail" v-model="propertyForm.billToEmail" type="email" required />
            </div>

            <div class="form-group">
                <label for="ccEmail">CC Email Address</label>
                <input id="ccEmail" v-model="propertyForm.ccEmail" type="email" />
            </div>
           
            <!-- Additional Fields -->
            <div class="form-group">
                <label for="startingInvoicePeriod">Starting Invoice Period</label>
                <input id="startingInvoicePeriod" v-model="propertyForm.startingInvoicePeriod" type="date" required />
            </div>

            <div class="form-group">
                <label for="startingPayCyclePeriod">Starting Pay Cycle Period</label>
                <input id="startingPayCyclePeriod" v-model="propertyForm.startingPayCyclePeriod" type="date" required />
            </div>

            <div class="form-group">
                <label for="startingPayDate">Starting Pay Date</label>
                <input id="startingPayDate" v-model="propertyForm.startingPayDate" type="date" required />
            </div>

            <div class="form-group">
                <label for="payOccurrence">Pay Occurrence</label>
                <select id="payOccurrence" v-model="propertyForm.payOccurrence" required>
                    <option value="">Select Pay Occurrence</option>
                    <option value="1">1 Week</option>
                    <option value="2">2 Weeks</option>
                    <option value="3">3 Weeks</option>
                    <option value="4">4 Weeks</option>
                </select>
            </div>

            <div class="form-group" v-if="endPayDate">
                <label>End Pay Date Period</label>
                <input :value="endPayDate" type="date" readonly class="readonly-field" />
            </div>

            <!-- Departments Section -->
            <div class="departments-section">
                <div class="departments-header">
                    <h3>Departments</h3>
                    <button type="button" class="add-dept-button" @click="addDepartment">
                        Add Department
                    </button>
                </div>
                
                <div v-for="(dept, index) in propertyForm.departments" :key="index" class="department-item">
                    <div class="dept-fields">
                        <div class="form-group">
                            <label>Department Name</label>
                            <input 
                                v-model="dept.name" 
                                type="text" 
                                placeholder="e.g. Housekeeping, Maintenance" 
                                required 
                            />
                        </div>
                        <div class="form-group">
                            <label>Hourly Rate ($)</label>
                            <input 
                                v-model.number="dept.hourlyRate" 
                                type="number" 
                                step="0.01" 
                                min="0" 
                                placeholder="0.00" 
                                required 
                            />
                        </div>
                        <button 
                            type="button" 
                            class="remove-dept-button" 
                            @click="removeDepartment(index)"
                            :disabled="propertyForm.departments.length === 1"
                        >
                            Remove
                        </button>
                    </div>
                </div>
            </div>

            <div class="form-actions">
                <button type="submit" class="save-button">
                    {{ isEditing ? 'Update Property' : 'Add Property' }}
                </button>
            </div>
        </form>
    </section>

    <!-- Properties Table -->
    <section class="properties-table-section">
        <div class="table-wrap">
            <table>
                <thead>
                    <tr>
                        <th>Property Name</th>
                        <th>Address</th>
                        <th>Bill To</th>
                        <th>Bill To Email</th>
                        <th>Departments</th>
                        <th>Date Created</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="property in properties" :key="property.id">
                        <td>{{ property.propertyName }}</td>
                        <td>{{ property.address }}</td>
                        <td>{{ property.billTo }}</td>
                        <td>{{ property.billToEmail || '—' }}</td>
                        <td>
                            <div class="departments-list">
                                <span 
                                    v-for="dept in property.departments" 
                                    :key="dept.name" 
                                    class="dept-tag"
                                >
                                    {{ dept.name }} (${{ dept.hourlyRate }}/hr)
                                </span>
                            </div>
                        </td>
                        <td>{{ formatDate(property.createdAt) }}</td>
                        <td>
                            <button class="action-button edit" @click="startEdit(property)">Edit</button>
                            <button class="action-button delete" @click="deleteProperty(property.id)">
                                Delete
                            </button>
                        </td>
                    </tr>
                    <tr v-if="properties.length === 0">
                        <td colspan="7" class="no-data">No properties found.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>



<style scoped>
/* Header */
.overview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
}

.overview-header h1 {
    font-size: 24px;
    color: #1f2937;
}

.add-button {
    background-color: #2563eb;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.add-button:hover {
    background-color: #1e40af;
}

/* Form Section */
.form-section {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 32px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-section h2 {
    margin-bottom: 16px;
    font-size: 20px;
    color: #111827;
}

.form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
}

.form-group label {
    margin-bottom: 4px;
    font-size: 14px;
    color: #374151;
    font-weight: 500;
}

.form-group input,
.form-group textarea {
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 14px;
}

.form-group textarea {
    resize: vertical;
}

/* Departments Section */
.departments-section {
    margin: 20px 0;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #f9fafb;
}

.departments-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.departments-header h3 {
    margin: 0;
    font-size: 16px;
    color: #374151;
}

.add-dept-button {
    background-color: #10b981;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
}

.add-dept-button:hover {
    background-color: #059669;
}

.department-item {
    margin-bottom: 12px;
    padding: 12px;
    background-color: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
}

.dept-fields {
    display: grid;
    grid-template-columns: 1fr 120px auto;
    gap: 12px;
    align-items: end;
}

.remove-dept-button {
    background-color: #ef4444;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    height: fit-content;
}

.remove-dept-button:hover:not(:disabled) {
    background-color: #dc2626;
}

.remove-dept-button:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
}

.form-actions {
    margin-top: 16px;
}

.save-button {
    background-color: #10b981;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.save-button:hover {
    background-color: #059669;
}

/* Properties Table Section */
.properties-table-section .table-wrap {
    overflow-x: auto;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

table {
    width: 100%;
    border-collapse: collapse;
}

thead {
    background-color: #e5e7eb;
}

th,
td {
    padding: 12px 16px;
    text-align: left;
    color: #374151;
}

th {
    font-size: 12px;
    text-transform: uppercase;
    color: #6b7280;
}

tbody tr {
    border-bottom: 1px solid #e5e7eb;
}

.departments-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.dept-tag {
    background-color: #dbeafe;
    color: #1e40af;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 11px;
    white-space: nowrap;
}

.action-button {
    margin-right: 8px;
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
}

.action-button.edit {
    background-color: #3b82f6;
    color: white;
}

.action-button.edit:hover {
    background-color: #2563eb;
}

.action-button.delete {
    background-color: #ef4444;
    color: white;
}

.action-button.delete:hover {
    background-color: #dc2626;
}

.no-data {
    text-align: center;
    padding: 16px;
    color: #6b7280;
}

/* Responsive */
@media (max-width: 768px) {
    .overview-header {
        flex-direction: column;
        align-items: stretch;
    }

    .add-button {
        margin-top: 12px;
        width: 100%;
    }

    .dept-fields {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .form-section,
    .properties-table-section .table-wrap {
        box-shadow: none;
    }
}
</style>

<script setup>
import { notifyWarning, notifyError, confirmAction } from '@/services/notify'
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db, auth } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'vue-router'

// Router + Auth
const router = useRouter()

// Reactive state
const properties = ref([])
let unsubscribeSnapshot = null

// Form state
const showForm = ref(false)
const isEditing = ref(false)
const editingPropertyId = ref(null)
const propertyForm = reactive({
    propertyName: '',
    address: '',
    billTo: '',
    billToEmail: '',
    ccEmail: '',
    startingInvoicePeriod: '',
    startingPayCyclePeriod: '',
    startingPayDate: '',
    payOccurrence: '',
    departments: [
        { name: '', hourlyRate: 0 }
    ]
})

// Computed property to calculate End Pay Date
const endPayDate = computed(() => {
    if (!propertyForm.startingPayDate || !propertyForm.payOccurrence) {
        return ''
    }
    
    const startDate = new Date(propertyForm.startingPayDate)
    const weeksToAdd = parseInt(propertyForm.payOccurrence)
    const endDate = new Date(startDate)
    endDate.setDate(startDate.getDate() + (weeksToAdd * 7))
    
    return endDate.toISOString().split('T')[0]
})

// Listen for auth state and fetch properties
onMounted(() => {
    const unwatchAuth = onAuthStateChanged(auth, (user) => {
        if (!user) {
            router.replace({ name: 'Login' })
        } else {
            const propertiesCol = collection(db, 'properties')
            unsubscribeSnapshot = onSnapshot(propertiesCol, (snapshot) => {
                properties.value = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
            })
        }
        unwatchAuth()
    })
})

onUnmounted(() => {
    if (unsubscribeSnapshot) {
        unsubscribeSnapshot()
    }
})

// Toggle the add/edit form
function toggleForm() {
    if (isEditing.value) {
        resetForm()
    }
    showForm.value = !showForm.value
}

// Add a new department field
function addDepartment() {
    propertyForm.departments.push({ name: '', hourlyRate: 0 })
}

// Remove a department field
function removeDepartment(index) {
    if (propertyForm.departments.length > 1) {
        propertyForm.departments.splice(index, 1)
    }
}

// Prepare form for editing a property
function startEdit(property) {
    isEditing.value = true
    editingPropertyId.value = property.id

    // Fill in existing data
    propertyForm.propertyName = property.propertyName
    propertyForm.address = property.address
    propertyForm.billTo = property.billTo
    propertyForm.billToEmail = property.billToEmail || ''
    propertyForm.ccEmail = property.ccEmail || ''
    propertyForm.startingInvoicePeriod = property.startingInvoicePeriod || ''
    propertyForm.startingPayCyclePeriod = property.startingPayCyclePeriod || ''
    propertyForm.startingPayDate = property.startingPayDate || ''
    propertyForm.payOccurrence = property.payOccurrence || ''
    propertyForm.departments = property.departments ? property.departments.map(dept => ({ ...dept })) : [{ name: '', hourlyRate: 0 }]

    showForm.value = true
}

// Save (create or update) a property
async function saveProperty() {
    // Validation
    if (
        !propertyForm.propertyName.trim() ||
        !propertyForm.address.trim() ||
        !propertyForm.billTo.trim() ||
        !propertyForm.billToEmail.trim() ||
        !propertyForm.startingInvoicePeriod ||
        !propertyForm.startingPayCyclePeriod ||
        !propertyForm.startingPayDate ||
        !propertyForm.payOccurrence ||
        propertyForm.departments.length === 0
    ) {
        notifyWarning('Please fill in all required fields.')
        return
    }

    // Validate departments
    for (const dept of propertyForm.departments) {
        if (!dept.name.trim() || dept.hourlyRate <= 0) {
            notifyWarning('Please fill in all department names and provide valid hourly rates.')
            return
        }
    }

    const payload = {
        propertyName: propertyForm.propertyName.trim(),
        address: propertyForm.address.trim(),
        billTo: propertyForm.billTo.trim(),
        billToEmail: propertyForm.billToEmail.trim(),
        ccEmail: propertyForm.ccEmail.trim(),
        startingInvoicePeriod: propertyForm.startingInvoicePeriod,
        startingPayCyclePeriod: propertyForm.startingPayCyclePeriod,
        startingPayDate: propertyForm.startingPayDate,
        payOccurrence: propertyForm.payOccurrence,
        endPayDate: endPayDate.value,
        departments: propertyForm.departments.map(dept => ({
            name: dept.name.trim(),
            hourlyRate: parseFloat(dept.hourlyRate)
        })),
        updatedAt: serverTimestamp()
    }

    // Add createdAt only for new properties
    if (!isEditing.value) {
        payload.createdAt = serverTimestamp()
    }

    try {
        if (isEditing.value && editingPropertyId.value) {
            const propertyRef = doc(db, 'properties', editingPropertyId.value)
            await updateDoc(propertyRef, payload)
        } else {
            await addDoc(collection(db, 'properties'), payload)
        }
        resetForm()
    } catch (e) {
        console.error('Error saving property:', e)
        notifyError('The property could not be saved. Please try again.')
    }
}

// Delete a property
async function deleteProperty(propertyId) {
    if (!(await confirmAction({ title: 'Delete this property?', text: 'This cannot be undone.', confirmText: 'Delete', danger: true }))) return
    try {
        await deleteDoc(doc(db, 'properties', propertyId))
    } catch (e) {
        console.error('Error deleting property:', e)
        notifyError('The property could not be deleted. Please try again.')
    }
}

// Reset form
function resetForm() {
    propertyForm.propertyName = ''
    propertyForm.address = ''
    propertyForm.billTo = ''
    propertyForm.billToEmail = ''
    propertyForm.ccEmail = ''
    propertyForm.startingInvoicePeriod = ''
    propertyForm.startingPayCyclePeriod = ''
    propertyForm.startingPayDate = ''
    propertyForm.payOccurrence = ''
    propertyForm.departments = [{ name: '', hourlyRate: 0 }]
    isEditing.value = false
    editingPropertyId.value = null
    showForm.value = false
}

// Format Firestore Timestamp to "MMM D, YYYY"
function formatDate(val) {
    if (!val) return '—'
    const d = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
}
</script>
<style scoped>
/* Header */
.overview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
}

.overview-header h1 {
    font-size: 24px;
    color: #1f2937;
}

.add-button {
    background-color: #2563eb;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.add-button:hover {
    background-color: #1e40af;
}

/* Form Section */
.form-section {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 32px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-section h2 {
    margin-bottom: 16px;
    font-size: 20px;
    color: #111827;
}

.form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
}

.form-group label {
    margin-bottom: 4px;
    font-size: 14px;
    color: #374151;
    font-weight: 500;
}

.form-group input,
.form-group textarea,
.form-group select {
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 14px;
}

.form-group textarea {
    resize: vertical;
}

.form-group select {
    background-color: white;
    cursor: pointer;
}

.form-group select:focus,
.form-group input:focus,
.form-group textarea:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* Readonly field styling */
.readonly-field {
    background-color: #f3f4f6 !important;
    color: #6b7280;
    cursor: not-allowed;
}

.readonly-field:focus {
    outline: none;
    box-shadow: none !important;
    border-color: #d1d5db !important;
}

/* Departments Section */
.departments-section {
    margin: 20px 0;
    padding: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #f9fafb;
}

.departments-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.departments-header h3 {
    margin: 0;
    font-size: 16px;
    color: #374151;
}

.add-dept-button {
    background-color: #10b981;
    color: white;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s ease;
}

.add-dept-button:hover {
    background-color: #059669;
}

.department-item {
    margin-bottom: 12px;
    padding: 12px;
    background-color: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
}

.dept-fields {
    display: grid;
    grid-template-columns: 1fr 120px auto;
    gap: 12px;
    align-items: end;
}

.remove-dept-button {
    background-color: #ef4444;
    color: white;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    height: fit-content;
    transition: background-color 0.2s ease;
}

.remove-dept-button:hover:not(:disabled) {
    background-color: #dc2626;
}

.remove-dept-button:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
}

.form-actions {
    margin-top: 16px;
}

.save-button {
    background-color: #10b981;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.save-button:hover {
    background-color: #059669;
}

/* Properties Table Section */
.properties-table-section .table-wrap {
    overflow-x: auto;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

table {
    width: 100%;
    border-collapse: collapse;
}

thead {
    background-color: #e5e7eb;
}

th,
td {
    padding: 12px 16px;
    text-align: left;
    color: #374151;
}

th {
    font-size: 12px;
    text-transform: uppercase;
    color: #6b7280;
    font-weight: 600;
}

tbody tr {
    border-bottom: 1px solid #e5e7eb;
}

tbody tr:hover {
    background-color: #f9fafb;
}

.departments-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.dept-tag {
    background-color: #dbeafe;
    color: #1e40af;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 11px;
    white-space: nowrap;
    font-weight: 500;
}

.action-button {
    margin-right: 8px;
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
}

.action-button.edit {
    background-color: #3b82f6;
    color: white;
}

.action-button.edit:hover {
    background-color: #2563eb;
}

.action-button.delete {
    background-color: #ef4444;
    color: white;
}

.action-button.delete:hover {
    background-color: #dc2626;
}

.no-data {
    text-align: center;
    padding: 16px;
    color: #6b7280;
    font-style: italic;
}

/* Focus states for better accessibility */
.add-button:focus,
.save-button:focus,
.add-dept-button:focus,
.remove-dept-button:focus,
.action-button:focus {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
}

/* Responsive */
@media (max-width: 768px) {
    .overview-header {
        flex-direction: column;
        align-items: stretch;
    }

    .add-button {
        margin-top: 12px;
        width: 100%;
    }

    .dept-fields {
        grid-template-columns: 1fr;
        gap: 8px;
    }

    .form-section,
    .properties-table-section .table-wrap {
        box-shadow: none;
        border-radius: 0;
    }

    .form-section {
        margin: 0 -16px 32px -16px;
        padding: 16px;
    }

    .properties-table-section .table-wrap {
        margin: 0 -16px;
    }

    .table-wrap {
        border-radius: 0;
    }

    th,
    td {
        padding: 8px 12px;
        font-size: 12px;
    }

    .action-button {
        display: block;
        margin-bottom: 4px;
        margin-right: 0;
        width: 100%;
    }

    .dept-tag {
        font-size: 10px;
        padding: 1px 4px;
    }
}

@media (max-width: 480px) {
    .overview-header h1 {
        font-size: 20px;
    }

    .form-section h2 {
        font-size: 18px;
    }

    .departments-header {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .add-dept-button {
        width: 100%;
    }

    .departments-list {
        flex-direction: column;
        gap: 2px;
    }

    .dept-tag {
        align-self: flex-start;
    }
}
</style>